import cx from "classnames";
import React, { forwardRef } from "react";

type TagName = "button" | "a";

interface ButtonProps {
  tagName: TagName;
  size?: "sm" | "lg";
  label?: string;
  variant?: "primary" | "secondary";
  disabled?: boolean | string;
  className?: string;
  type?: "button" | "submit";
  onClick?: () => void;
  children?: React.ReactElement | string;
}

const interactiveTagMap: Record<TagName, TagName> = {
  button: "button",
  a: "a",
};

const variantMap: Record<string, string> = {
  primary: "btn btn--primary",
  secondary: "btn btn--secondary",
};

const sizeMap: Record<string, string> = {
  sm: "btn--sm",
  lg: "btn--lg",
};

export const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      tagName,
      className = "",
      size = null,
      type = "button",
      label,
      variant = null,
      disabled = false,
      onClick = () => undefined,
      children = null,
      ...rest
    },
    ref
  ) => {
    const ButtonTag = interactiveTagMap[tagName] || "button";

    // Is interactive tag?!
    const isInteractive = tagName === "button" || tagName === "a";

    // Disabled attr & class
    const isDisable = !!disabled && isInteractive;
    const disableClass = typeof disabled === "string" ? disabled : "";

    return (
      <ButtonTag
        ref={ref as never}
        className={cx(
          variantMap[variant || ""],
          sizeMap[size || ""],
          className,
          disableClass
        )}
        type={type}
        disabled={isDisable}
        onClick={onClick}
        {...rest}
      >
        {children}
        {label}
      </ButtonTag>
    );
  }
);

Button.displayName = "Button";
